import React, { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useActivities, useStudents } from '@/hooks/useSupabaseData';
import { useAppSettings } from '@/hooks/useAppSettings';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';
import { 
  CheckCircle2, 
  Search, 
  ArrowLeft, 
  Clock, 
  Users, 
  GraduationCap,
  Activity,
  LayoutGrid,
  List,
  Check,
  X
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';

type Step = 'activity' | 'student' | 'confirm';
type ViewMode = 'wizard' | 'quick';

export const MobileQuickAttendance = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('quick');
  const [step, setStep] = useState<Step>('activity');
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [selectedStudent, setSelectedStudent] = useState<any>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [registering, setRegistering] = useState(false);
  const [attendanceMap, setAttendanceMap] = useState<Record<string, boolean>>({});
  const [currentTime, setCurrentTime] = useState<string>('');
  
  const { activities, loading: activitiesLoading } = useActivities();
  const { students, loading: studentsLoading, refetch: refetchStudents } = useStudents();
  const { getSetting } = useAppSettings();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Real-time clock
  useEffect(() => {
    const updateTime = () => {
      const timeZone = 'Europe/Madrid';
      const madridDate = toZonedTime(new Date(), timeZone);
      const timeString = format(madridDate, 'HH:mm:ss', { locale: es });
      setCurrentTime(timeString);
    };

    updateTime(); // Initial call
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Helper para obtener el tiempo de forma segura
  const getActivityTime = (activity: any) => {
    if (activity.time) return activity.time;
    if (activity.start_time && activity.end_time) {
      return `${activity.start_time} - ${activity.end_time}`;
    }
    return 'Horario no disponible';
  };

  // Filtrar actividades activas (considerar tiempo anticipado)
  const todayActivities = useMemo(() => {
    const earlyMinutes = getSetting('early_attendance_minutes') || 30; // Por defecto 30 minutos
    const currentTime = new Date();
    
    return activities.filter((activity: any) => {
      if (activity.status !== 'active') return false;
      
      const jsDay = currentTime.getDay();
      const dbDay = jsDay === 0 ? 7 : jsDay;
      const isScheduledToday = activity.days_of_week?.includes(dbDay);
      
      // Si la actividad está programada para hoy, verificar si ya puede registrarse
      if (isScheduledToday && activity.start_time) {
        const [hours, minutes] = activity.start_time.split(':').map(Number);
        const activityStart = new Date(currentTime);
        activityStart.setHours(hours, minutes, 0, 0);
        
        // Calcular tiempo de registro anticipado
        const earliestRegistration = new Date(activityStart.getTime() - (earlyMinutes * 60 * 1000));
        
        // Permitir registro si es después del tiempo anticipado y antes del final del día
        const endOfDay = new Date(currentTime);
        endOfDay.setHours(23, 59, 59, 999);
        
        return currentTime >= earliestRegistration && currentTime <= endOfDay;
      }
      
      // Para actividades no programadas hoy, permitir registro manual
      return true;
    });
  }, [activities, getSetting]);

  // Helper para saber si una actividad es de hoy y si está en horario de registro
  const getActivityStatus = (activity: any) => {
    const earlyMinutes = getSetting('early_attendance_minutes') || 30;
    const currentTime = new Date();
    const jsDay = currentTime.getDay();
    const dbDay = jsDay === 0 ? 7 : jsDay;
    const isScheduledToday = activity.days_of_week?.includes(dbDay);
    
    if (isScheduledToday && activity.start_time) {
      const [hours, minutes] = activity.start_time.split(':').map(Number);
      const activityStart = new Date(currentTime);
      activityStart.setHours(hours, minutes, 0, 0);
      
      const earliestRegistration = new Date(activityStart.getTime() - (earlyMinutes * 60 * 1000));
      
      if (currentTime >= earliestRegistration && currentTime < activityStart) {
        return { type: 'early', label: 'Registro anticipado' };
      } else if (currentTime >= activityStart) {
        return { type: 'current', label: 'EN CURSO' };
      } else {
        return { type: 'future', label: `Disponible en ${Math.ceil((earliestRegistration.getTime() - currentTime.getTime()) / (1000 * 60))} min` };
      }
    }
    
    return { type: 'manual', label: 'Fuera de horario' };
  };

  // Filtrar alumnos inscritos en la actividad seleccionada que NO tienen asistencia hoy
  const enrolledStudents = useMemo(() => {
    if (!selectedActivity) return [];
    
    const timeZone = 'Europe/Madrid';
    const madridDate = toZonedTime(new Date(), timeZone);
    const today = format(madridDate, 'yyyy-MM-dd', { locale: es });
    
    return students.filter((student: any) => {
      // Verificar que está inscrito en la actividad
      const isEnrolled = student.student_activities?.some((sa: any) => 
        String(sa.activity_id) === String(selectedActivity.id) && sa.status === 'active'
      );
      
      if (!isEnrolled) return false;
      
      // Verificar que NO tiene asistencia registrada hoy para esta actividad
      const hasAttendanceToday = student.attendances?.some((att: any) => 
        String(att.activity_id) === String(selectedActivity.id) && 
        att.date === today
      );
      
      return !hasAttendanceToday;
    });
  }, [students, selectedActivity]);

  // Filtrar alumnos por búsqueda
  const filteredStudents = useMemo(() => {
    if (!searchTerm.trim()) return enrolledStudents;
    const searchLower = searchTerm.toLowerCase();
    return enrolledStudents.filter((student: any) =>
      student.name.toLowerCase().includes(searchLower) ||
      student.class.toLowerCase().includes(searchLower)
    );
  }, [enrolledStudents, searchTerm]);

  const handleActivitySelect = (activity: any) => {
    setSelectedActivity(activity);
    if (viewMode === 'wizard') {
      setStep('student');
    }
    // Limpiar el mapa de asistencia al cambiar de actividad
    setAttendanceMap({});
  };

  const handleStudentSelect = (student: any) => {
    setSelectedStudent(student);
    setStep('confirm');
  };

  const handleToggleAttendance = async (studentId: string) => {
    try {
      setRegistering(true);
      const timeZone = 'Europe/Madrid';
      const madridDate = toZonedTime(new Date(), timeZone);
      const today = format(madridDate, 'yyyy-MM-dd', { locale: es });
      const now = format(madridDate, 'HH:mm:ss');
      const newStatus = !attendanceMap[studentId];

      // Verificar si ya existe asistencia hoy
      const { data: existingAttendance } = await supabase
        .from('attendances')
        .select('*')
        .eq('student_id', studentId)
        .eq('activity_id', selectedActivity.id)
        .eq('date', today);

      if (existingAttendance && existingAttendance.length > 0) {
        // Actualizar asistencia existente
        const { error } = await supabase
          .from('attendances')
          .update({
            status: newStatus ? 'present' : 'absent',
            time: now,
            notes: null
          })
          .eq('id', existingAttendance[0].id);

        if (error) throw error;
      } else {
        // Crear nueva asistencia
        const { error } = await supabase
          .from('attendances')
          .insert({
            student_id: studentId,
            activity_id: selectedActivity.id,
            date: today,
            time: now,
            status: newStatus ? 'present' : 'absent'
          });

        if (error) throw error;
      }

      // Actualizar el estado local
      setAttendanceMap(prev => ({
        ...prev,
        [studentId]: newStatus
      }));

      // Recargar los estudiantes para actualizar la lista
      refetchStudents();

      toast({
        title: newStatus ? "✅ Asistencia registrada" : "❌ Asistencia removida",
        description: `Registro actualizado correctamente a las ${now}`,
      });

      refetchStudents();
    } catch (error) {
      console.error('Error registering attendance:', error);
      toast({
        title: "Error",
        description: "No se pudo registrar la asistencia. Inténtalo de nuevo.",
        variant: "destructive"
      });
    } finally {
      setRegistering(false);
    }
  };

  const handleRegisterAttendance = async () => {
    if (!selectedActivity || !selectedStudent) return;
    
    try {
      setRegistering(true);
      const timeZone = 'Europe/Madrid';
      const madridDate = toZonedTime(new Date(), timeZone);
      const today = format(madridDate, 'yyyy-MM-dd', { locale: es });
      const now = format(madridDate, 'HH:mm:ss');

      // Verificar si ya existe asistencia hoy
      const { data: existingAttendance } = await supabase
        .from('attendances')
        .select('*')
        .eq('student_id', selectedStudent.id)
        .eq('activity_id', selectedActivity.id)
        .eq('date', today);

      if (existingAttendance && existingAttendance.length > 0) {
        // Actualizar asistencia existente
        const { error } = await supabase
          .from('attendances')
          .update({
            status: 'present',
            time: now,
            notes: null
          })
          .eq('id', existingAttendance[0].id);

        if (error) throw error;
      } else {
        // Crear nueva asistencia
        const { error } = await supabase
          .from('attendances')
          .insert({
            student_id: selectedStudent.id,
            activity_id: selectedActivity.id,
            date: today,
            time: now,
            status: 'present'
          });

        if (error) throw error;
      }

      toast({
        title: "✅ Asistencia registrada",
        description: `${selectedStudent.name} marcado como presente en ${selectedActivity.name} a las ${now}`,
      });

      // Reset del flujo
      if (viewMode === 'wizard') {
        setStep('activity');
        setSelectedActivity(null);
        setSelectedStudent(null);
      }
      setSearchTerm('');
      refetchStudents();

    } catch (error) {
      console.error('Error registering attendance:', error);
      toast({
        title: "Error",
        description: "No se pudo registrar la asistencia. Inténtalo de nuevo.",
        variant: "destructive"
      });
    } finally {
      setRegistering(false);
    }
  };

  const resetFlow = () => {
    setStep('activity');
    setSelectedActivity(null);
    setSelectedStudent(null);
    setSearchTerm('');
    setAttendanceMap({});
  };

  const goBack = () => {
    if (step === 'student') {
      setStep('activity');
      setSelectedActivity(null);
    } else if (step === 'confirm') {
      setStep('student');
      setSelectedStudent(null);
    }
  };

  return (
    <div className={`space-y-6 pb-8 ${isMobile ? 'px-2' : ''}`}>
      {/* Header con navegación */}
      <Card className="shadow-card">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            {viewMode === 'wizard' && step !== 'activity' && (
              <Button variant="ghost" size={isMobile ? "default" : "sm"} onClick={goBack}>
                <ArrowLeft className={`${isMobile ? 'w-5 h-5' : 'w-4 h-4'}`} />
              </Button>
            )}
            <div className="flex-1">
              <CardTitle className={`${isMobile ? 'text-xl' : 'text-lg'} flex items-center gap-2`}>
                <CheckCircle2 className="w-5 h-5 text-primary" />
                Registro Rápido
              </CardTitle>
              <div className="flex items-center gap-2 mt-1">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span className="text-sm font-mono font-medium text-primary bg-primary/10 px-2 py-1 rounded">
                  {currentTime}
                </span>
                <span className="text-xs text-muted-foreground">
                  Hora actual
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                variant={viewMode === 'quick' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('quick')}
              >
                <List className="w-4 h-4 mr-2" />
                Lista
              </Button>
              <Button
                variant={viewMode === 'wizard' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('wizard')}
              >
                <LayoutGrid className="w-4 h-4 mr-2" />
                Paso a paso
              </Button>
            </div>
          </div>
          
          {viewMode === 'wizard' && (
            <div className="flex items-center gap-2 mt-2">
              <Badge variant={step === 'activity' ? 'default' : 'secondary'} className="text-xs">
                1. Actividad
              </Badge>
              <div className="w-2 h-0.5 bg-muted" />
              <Badge variant={step === 'student' ? 'default' : 'secondary'} className="text-xs">
                2. Alumno
              </Badge>
              <div className="w-2 h-0.5 bg-muted" />
              <Badge variant={step === 'confirm' ? 'default' : 'secondary'} className="text-xs">
                3. Confirmar
              </Badge>
            </div>
          )}
        </CardHeader>
      </Card>

      {/* Vista Rápida */}
      {viewMode === 'quick' && (
        <div className="space-y-4">
          {/* Selector de Actividad */}
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-primary" />
                Selecciona una actividad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {todayActivities.map((activity: any) => (
                  <div
                    key={activity.id}
                    onClick={() => handleActivitySelect(activity)}
                    className={`p-4 border rounded-lg transition-colors cursor-pointer ${
                      selectedActivity?.id === activity.id 
                        ? 'bg-primary/10 border-primary'
                        : 'bg-card hover:bg-accent/50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold">{activity.name}</h3>
                          {(() => {
                            const status = getActivityStatus(activity);
                            switch (status.type) {
                              case 'current':
                                return <Badge variant="default" className="bg-green-600 text-white text-xs">{status.label}</Badge>;
                              case 'early':
                                return <Badge variant="default" className="bg-blue-600 text-white text-xs">{status.label}</Badge>;
                              case 'future':
                                return <Badge variant="secondary" className="text-xs">{status.label}</Badge>;
                              case 'manual':
                                return <Badge variant="outline" className="text-xs">{status.label}</Badge>;
                              default:
                                return null;
                            }
                          })()}
                        </div>
                        <p className="text-sm text-muted-foreground">{getActivityTime(activity)}</p>
                      </div>
                      <Badge variant="outline">
                        {activity.currentStudents}/{activity.maxStudents}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Lista de Alumnos con Búsqueda */}
          {selectedActivity && (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  {filteredStudents.length === 0 ? (
                    <span className="text-green-600">✅ Actividad completa</span>
                  ) : (
                    <>Alumnos pendientes ({filteredStudents.length})</>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Barra de búsqueda */}
                  <div className="relative">
                    <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Buscar por nombre o clase..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>

                  {/* Lista de alumnos con toggle de asistencia */}
                  {filteredStudents.length === 0 ? (
                    <div className="text-center py-8">
                      <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-600" />
                      <h3 className="text-lg font-semibold text-green-600 mb-2">¡Asistencia completa!</h3>
                      <p className="text-muted-foreground">Todos los estudiantes de esta actividad ya han sido registrados hoy.</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredStudents.map((student: any) => (
                      <div
                        key={student.id}
                        className={`flex items-center justify-between p-4 border rounded-lg transition-colors ${
                          attendanceMap[student.id] ? 'bg-primary/10 border-primary' : 'bg-card'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <Avatar>
                            <AvatarFallback>
                              {student.name.split(' ').map((n: string) => n[0]).join('')}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <h4 className="font-medium">{student.name}</h4>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <GraduationCap className="w-3 h-3" />
                              {student.class}
                            </div>
                          </div>
                        </div>
                        <Button
                          variant={attendanceMap[student.id] ? "default" : "outline"}
                          size="sm"
                          onClick={() => handleToggleAttendance(student.id)}
                          disabled={registering}
                          className={`min-w-[100px] ${
                            attendanceMap[student.id] ? 'bg-primary hover:bg-primary/90' : ''
                          }`}
                        >
                          {attendanceMap[student.id] ? (
                            <>
                              <Check className="w-4 h-4 mr-2" />
                              Presente
                            </>
                          ) : (
                            <>
                              <X className="w-4 h-4 mr-2" />
                              Ausente
                            </>
                          )}
                        </Button>
                      </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Vista Paso a Paso (Wizard) - Mantener el código existente */}
      {viewMode === 'wizard' && (
        <>
          {/* Paso 1: Seleccionar Actividad */}
          {step === 'activity' && (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="w-5 h-5 text-primary" />
                  Selecciona una actividad
                </CardTitle>
              </CardHeader>
              <CardContent>
                {activitiesLoading ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Clock className="w-8 h-8 mx-auto mb-2 animate-spin" />
                    Cargando actividades...
                  </div>
                ) : todayActivities.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Activity className="w-8 h-8 mx-auto mb-2" />
                    No hay actividades programadas para hoy
                  </div>
                ) : (
                  <div className="space-y-3">
                    {todayActivities.map((activity: any) => (
                      <div
                        key={activity.id}
                        onClick={() => handleActivitySelect(activity)}
                        className={`${isMobile ? 'p-5' : 'p-4'} border rounded-lg bg-card hover:bg-accent/50 transition-colors cursor-pointer active:scale-[0.98]`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className={`font-semibold ${isMobile ? 'text-lg' : ''}`}>{activity.name}</h3>
                            <p className="text-sm text-muted-foreground">{getActivityTime(activity)}</p>
                          </div>
                          <div className="text-right">
                            <Badge variant="outline" className="mb-1">
                              {activity.currentStudents}/{activity.maxStudents}
                            </Badge>
                            <div className="text-xs text-muted-foreground">
                              Presentes
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Paso 2: Seleccionar Alumno */}
          {step === 'student' && selectedActivity && (
            <div className="space-y-4">
              {/* Info actividad seleccionada */}
              <Card className="shadow-card">
                <CardContent className={`${isMobile ? 'pt-5' : 'pt-4'}`}>
                  <div className="flex items-center gap-3">
                    <div className={`${isMobile ? 'w-12 h-12' : 'w-10 h-10'} rounded-lg bg-primary/10 flex items-center justify-center`}>
                      <Activity className={`${isMobile ? 'w-6 h-6' : 'w-5 h-5'} text-primary`} />
                    </div>
                    <div>
                      <h3 className={`font-semibold ${isMobile ? 'text-lg' : ''}`}>{selectedActivity.name}</h3>
                      <p className="text-sm text-muted-foreground">{getActivityTime(selectedActivity)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Paso 3: Confirmar Asistencia */}
          {step === 'confirm' && selectedActivity && selectedStudent && (
            <Card className="shadow-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-primary" />
                  Confirmar Asistencia
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12">
                      <AvatarFallback>
                        {selectedStudent.name.split(' ').map((n: string) => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <h4 className="font-semibold text-lg">{selectedStudent.name}</h4>
                      <div className="text-sm text-muted-foreground">{selectedStudent.class}</div>
                    </div>
                  </div>
                  <Button
                    onClick={handleRegisterAttendance}
                    disabled={registering}
                    className="w-full"
                  >
                    {registering ? 'Registrando...' : 'Confirmar asistencia'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={resetFlow}
                    className="w-full"
                    disabled={registering}
                  >
                    Cancelar
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
